import { SnackbarContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import { styled, useTheme } from "@mui/material/styles";
import { forwardRef } from "react";

type TransactionStatus = "error" | "success" | "warning" | "info";

interface TransactionSnackbarProps {
  id: string;
  status: TransactionStatus;
  title: string;
  message?: string;
  closeSnackbar: (key: string | number) => void;
  onTap?: () => void;
}

const StatusIcons = {
  error: CloseIcon,
  success: CheckIcon,
  warning: WarningIcon,
  info: InfoIcon,
};

const StatusColors = {
  error: (theme: any) => theme.palette.error.main,
  success: (theme: any) => theme.palette.success.main,
  warning: (theme: any) => theme.palette.warning.main,
  info: (theme: any) => theme.palette.info.main,
};

// Create interfaces for custom props
interface StyledSnackbarContentProps {
  statusColor: string;
}

// Use the interface in the styled component
const StyledSnackbarContent = styled(SnackbarContent, {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<StyledSnackbarContentProps>(({ theme, statusColor }) => ({
  backgroundColor: "#2c2c2c",
  color: "white",
  borderLeft: `4px solid ${statusColor}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  "& .MuiSnackbarContent-message": {
    padding: 0,
  },
}));

const IconContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "statusColor",
})<{ statusColor: string }>(({ theme, statusColor }) => ({
  display: "flex",
  height: 32,
  width: 32,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: statusColor,
  color: theme.palette.common.white,
}));

export const TransactionSnackbar = forwardRef<
  HTMLDivElement,
  TransactionSnackbarProps
>(
  (
    {
      id,
      status,
      title,
      message = "Tap to view this transaction",
      closeSnackbar,
      onTap,
    },
    ref
  ) => {
    const theme = useTheme();
    const Icon = StatusIcons[status];
    const statusColor = StatusColors[status](theme);

    const handleClick = () => {
      onTap?.();
      closeSnackbar(Number(id));
    };

    return (
      <StyledSnackbarContent
        ref={ref}
        statusColor={statusColor}
        message={
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <IconContainer statusColor={statusColor}>
              <Icon fontSize="small" />
            </IconContainer>
            <div>
              <div className="font-semibold text-[14px]">{title}</div>
              {message && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(255,255,255,0.7)",
                    whiteSpace: "pre-line",
                  }}
                >
                  {message}
                </div>
              )}
            </div>
          </div>
        }
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={() => closeSnackbar(id)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        onClick={handleClick}
      />
    );
  }
);

TransactionSnackbar.displayName = "TransactionSnackbar";

export default TransactionSnackbar;
